import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setNodeAttributes } from '../../../../../redux/reducers/global/node/nodeSlice';
import EditableInspectorLabel from '../EditableInspectorLabel';

const MIN_DIMENSION = 1;
const MAX_DIMENSION = 4000;
const MIN_STROKE = 1;
const MAX_STROKE = 24;
const MIN_DIVISIONS = 1;
const MAX_DIVISIONS = 200;

const BORDER_OPTIONS = [
  { value: 'solid', label: 'Sólido' },
  { value: 'dashed', label: 'Segmentado' },
  { value: 'dotted', label: 'Punteado' },
  { value: 'dashdot', label: 'Guion y punto' },
];

const clampStroke = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return MIN_STROKE;
  }
  return Math.min(MAX_STROKE, Math.max(MIN_STROKE, Math.round(numeric)));
};

const clampDimension = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return MIN_DIMENSION;
  }
  return Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, Math.round(numeric)));
};

const clampDivision = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return MIN_DIVISIONS;
  }
  return Math.min(MAX_DIVISIONS, Math.max(MIN_DIVISIONS, Math.round(numeric)));
};

const normalizeBorderStyle = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : 'solid';
  if (normalized === 'dash-dot' || normalized === 'dashdot' || normalized === 'dash_point') {
    return 'dashdot';
  }
  if (normalized === 'pointed') {
    return 'dotted';
  }
  if (BORDER_OPTIONS.some((opt) => opt.value === normalized)) {
    return normalized;
  }
  return 'solid';
};

const getAttributeValue = (attributes, key, fallback) =>
  attributes?.[key]?.value ?? fallback;

const BoxInspector = () => {
  const dispatch = useDispatch();
  const selectedNodeId = useSelector((state) => state.node.selectedNodeId);
  const nodes = useSelector((state) => state.node.nodes) || [];
  const currentNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );

  const attributes = currentNode?.data?.attributes || {};
  const strokeWidth = clampStroke(getAttributeValue(attributes, 'strokeWidth', 3));
  const width = clampDimension(getAttributeValue(attributes, 'width', 240));
  const height = clampDimension(getAttributeValue(attributes, 'height', 160));
  const columns = clampDivision(getAttributeValue(attributes, 'columns', 1));
  const rows = clampDivision(getAttributeValue(attributes, 'rows', 1));
  const borderStyle = normalizeBorderStyle(getAttributeValue(attributes, 'borderStyle', 'solid'));

  const applyAttribute = (key, value) => {
    if (!selectedNodeId) return;
    const previous = attributes[key] || {};
    dispatch(
      setNodeAttributes({
        nodeId: selectedNodeId,
        attributes: {
          [key]: {
            ...previous,
            value,
            label: previous.label || ({
              strokeWidth: 'Grosor de línea (px)',
              width: 'Ancho (px)',
              height: 'Alto (px)',
              columns: 'Columnas',
              rows: 'Filas',
              borderStyle: 'Estilo de borde',
            }[key] || key),
            isVisible: previous.isVisible ?? true,
          },
        },
      }),
    );
  };

  const handleLabelCommit = (key, nextLabel, fallbackLabel) => {
    if (!selectedNodeId) return;
    const previous = attributes[key] || {};
    dispatch(
      setNodeAttributes({
        nodeId: selectedNodeId,
        attributes: {
          [key]: {
            ...previous,
            label: nextLabel ?? fallbackLabel,
            value: previous.value ?? getAttributeValue(attributes, key, ''),
            isVisible: previous.isVisible ?? true,
          },
        },
      }),
    );
  };

  if (!currentNode) {
    return null;
  }

  return (
    <div className="box-inspector">
      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.width?.label}
          defaultLabel="Ancho (px)"
          onCommit={(nextLabel) => handleLabelCommit('width', nextLabel, 'Ancho (px)')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <input
              type="number"
              min={MIN_DIMENSION}
              max={MAX_DIMENSION}
              value={width}
              onChange={(event) => applyAttribute('width', clampDimension(event.target.value))}
              className="inspector__field-input-element"
            />
            <span className="inspector__field-input-unit">px</span>
          </div>
        </div>
      </div>

      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.height?.label}
          defaultLabel="Alto (px)"
          onCommit={(nextLabel) => handleLabelCommit('height', nextLabel, 'Alto (px)')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <input
              type="number"
              min={MIN_DIMENSION}
              max={MAX_DIMENSION}
              value={height}
              onChange={(event) => applyAttribute('height', clampDimension(event.target.value))}
              className="inspector__field-input-element"
            />
            <span className="inspector__field-input-unit">px</span>
          </div>
        </div>
      </div>

      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.borderStyle?.label}
          defaultLabel="Estilo de borde"
          onCommit={(nextLabel) => handleLabelCommit('borderStyle', nextLabel, 'Estilo de borde')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              value={borderStyle}
              onChange={(event) => applyAttribute('borderStyle', normalizeBorderStyle(event.target.value))}
              className="inspector__select"
            >
              {BORDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.strokeWidth?.label}
          defaultLabel="Grosor de línea (px)"
          onCommit={(nextLabel) => handleLabelCommit('strokeWidth', nextLabel, 'Grosor de línea (px)')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <input
              type="number"
              min={MIN_STROKE}
              max={MAX_STROKE}
              value={strokeWidth}
              onChange={(event) => applyAttribute('strokeWidth', clampStroke(event.target.value))}
              className="inspector__field-input-element"
            />
            <span className="inspector__field-input-unit">px</span>
          </div>
        </div>
      </div>

      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.columns?.label}
          defaultLabel="Columnas"
          onCommit={(nextLabel) => handleLabelCommit('columns', nextLabel, 'Columnas')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <input
              type="number"
              min={MIN_DIVISIONS}
              max={MAX_DIVISIONS}
              value={columns}
              onChange={(event) => applyAttribute('columns', clampDivision(event.target.value))}
              className="inspector__field-input-element"
            />
          </div>
        </div>
      </div>

      <div className="inspector__field inspector__field--visible">
        <EditableInspectorLabel
          label={attributes.rows?.label}
          defaultLabel="Filas"
          onCommit={(nextLabel) => handleLabelCommit('rows', nextLabel, 'Filas')}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <input
              type="number"
              min={MIN_DIVISIONS}
              max={MAX_DIVISIONS}
              value={rows}
              onChange={(event) => applyAttribute('rows', clampDivision(event.target.value))}
              className="inspector__field-input-element"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxInspector;
